import { useTranslation } from "react-i18next";
import { SectionTitle, formatBalance } from "./fields";
import { formatDate } from "@/shared/lib/time";
import type { CustomerDetail } from "../types";

// Read-only account section — balance display + premium/developer status block.
// Extracted from the former single-file CustomerDrawer (task 0067.02) — behavior
// unchanged; write-ops live in OperationsSection (0063.04).
export function AccountPremiumBlock({ data }: { data: CustomerDetail }) {
  const { t } = useTranslation();
  return (
    <>
      {/* Read-only account section — balance display only; write-ops in 0063.04 */}
      <SectionTitle className="mt-2">{t("drawer.sectionAccount")}</SectionTitle>
      <div className="mb-5 flex flex-col gap-[7px]">
        <span className="text-[13px] font-semibold text-grey-700">{t("drawer.balance")}</span>
        <span className="text-[16px] font-bold text-grey-900">{formatBalance(data.balance)}</span>
      </div>

      {/* Read-only premium / developer status block */}
      <div className="mb-5 rounded-xl border border-grey-200 bg-grey-25 px-4 py-3.5 flex flex-col gap-2">
        {/* Premium status */}
        {data.is_premium && data.premium ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-pill bg-green px-2.5 py-0.5 text-[12px] font-bold text-white">
              {t("drawer.premium")}
            </span>
            <span className="text-[13px] text-grey-700">
              {t("drawer.premiumUntil", { date: formatDate(data.premium.end_date) })}
            </span>
            <span className="text-[12px] text-grey-500">
              {data.premium.is_free ? t("drawer.premiumFree") : t("drawer.premiumPaid")}
            </span>
            {data.premium.is_repeatable && (
              <span className="text-[12px] text-grey-400">· {t("drawer.premiumRepeatable")}</span>
            )}
          </div>
        ) : (
          <span className="text-[13px] text-grey-500">{t("drawer.freeTier")}</span>
        )}
        {/* Free premiums remaining */}
        <span className="text-[12px] text-grey-500">
          {t("drawer.freeRemaining", { count: data.free_premiums_count })}
        </span>
        {/* Developer marker — read-only; toggle is subtask 0063.04 */}
        {data.is_developer && (
          <span className="mt-1 w-fit rounded-pill border border-grey-300 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-grey-600">
            {t("drawer.developerBadge")}
          </span>
        )}
      </div>
    </>
  );
}
